/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Text, Icon, Radiobutton } from '@components/atoms'
import { IconPicker, Modal } from '@components/molecules'
import { DndTree } from '@components/systems'

import { blue, deepPurple, grey, pink } from '@mui/material/colors'
import { Path } from '@utils'

import {
	useAppDispatch,
	useAppSelector,
	EditMenuType,
	adminActions,
	MenuTreeNodeType,
	CrudType,
} from '@redux/index'
import { produce } from 'immer'

export const AdminEditMenuModal = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const menuOpenModal: string = useAppSelector(state => state.admin.menuOpenModal)
	const unduplicatedName: string = useAppSelector(state => state.admin.unduplicatedName)
	const menuTreeList = useAppSelector(state => state.admin.menuTreeList)
	const selectedMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)

	// state
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [modalInfo, setModalInfo] = React.useState({ target: '', title: '' })
	const [isCheckedRecommendedName, setIsCheckedRecommendedName] =
		React.useState<boolean>(false)
	const [tempSelectedMenuParent, setTempSelectedMenuParent] =
		React.useState<string>('root')

	// variables
	const isDir: boolean = editTempMenu.id.startsWith('MD-')
	const isAllowDeleteDir: boolean = crud.includes('D') !== null && isDir
	const isAllowDeleteObject: boolean = crud.includes('D') !== null && !isDir

	const showSubmit: boolean =
		(modalInfo.target === 'deletingDirSubmit' && isAllowDeleteDir) ||
		(modalInfo.target === 'deletingObjectSubmit' && isAllowDeleteObject) ||
		true
	const submitTitle: string = React.useMemo(
		() =>
			['menuTree', 'duplicatedNameAlert'].includes(modalInfo.target)
				? '선택'
				: ['deletingDirSubmit', 'deletingObjectSubmit'].includes(modalInfo.target)
				? '삭제'
				: '저장',
		[modalInfo.target],
	)
	const closeTitle: string = React.useMemo(
		() =>
			['duplicatedNameWithMovingAlert'].includes(modalInfo.target) ? '닫기' : '취소',
		[modalInfo.target],
	)

	React.useEffect(() => {
		if (menuOpenModal !== '') {
			onOpenModal(menuOpenModal)
		}
	}, [menuOpenModal])

	const onOpenModal = (target: string) => {
		const newModalInfo = produce(modalInfo, draft => {
			draft.target = target

			if (target === 'menuTree') {
				draft.title = '상위 메뉴 선택'
			} else if (target === 'iconPicker') {
				draft.title = '아이콘 선택'
			} else if (target === 'duplicatedNameAlert') {
				draft.title = '같은 이름의 폴더가 존재합니다.'
			} else if (target === 'updatingSubmit') {
				draft.title = '메뉴 변경 저장'
			} else if (target === 'duplicatedNameWithMovingAlert') {
				draft.title = '중복되는 이름 발견'
			} else if (target === 'deletingDirSubmit') {
				draft.title = '메뉴 삭제'
			} else if (target === 'deletingObjectSubmit') {
				draft.title = '메뉴 오브젝트 삭제'
			}
		})

		setModalInfo(newModalInfo)
		setShowModal(true)
	}

	const onCloseModal = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL(''))
		setShowModal(false)

		if (modalInfo.target === 'duplicatedNameWithMovingAlert') {
			reduxDispatch(adminActions.TRIGGER_GET_MENU_LIST(true))
		}
	}

	const onMenuTreeRadioChange = (value: string | number) => {
		const tempParentId: string = value.toString() ?? value
		setTempSelectedMenuParent(tempParentId)
	}

	const onIconPickerSubmit = (iconName: string) => {
		onCloseModal()

		const newMenu: EditMenuType = {
			...selectedMenu,
			icon: iconName,
		}

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	const onSubmit = () => {
		if (modalInfo.target === 'menuTree') {
			onSelectMenuTreeClick()
		} else if (modalInfo.target === 'duplicatedNameAlert') {
			onChangeMenuName()
		} else if (modalInfo.target === 'updatingSubmit') {
			onUpdatingMenuSubmit()
		} else if (modalInfo.target === 'deletingDirSubmit') {
			onDeletingMenuDirSubmit()
		} else if (modalInfo.target === 'deletingObjectSubmit') {
			onDeletingMenuObjectSubmit()
		}
	}

	const onSelectMenuTreeClick = () => {
		onCloseModal()

		const newPath: string = Path.getPathByParentIdAndName({
			menuTreeList: menuTreeList,
			parentId: tempSelectedMenuParent,
			name: selectedMenu.name,
		})
		const newUri: string = Path.getUriByPath(newPath)

		const newMenu: EditMenuType = {
			...selectedMenu,
			parent: tempSelectedMenuParent,
			path: newPath,
			uri: newUri,
		}

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	const onChangeMenuName = () => {
		const newPath: string = Path.getPathByName(selectedMenu.path, unduplicatedName)
		const newUri: string = Path.getUriByPath(newPath)
		const newMenu: EditMenuType = produce(selectedMenu, draft => {
			draft.name = unduplicatedName
			draft.path = newPath
			draft.uri = newUri
		})

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	const onUpdatingMenuSubmit = () => {
		onCloseModal()
		reduxDispatch(adminActions.TRIGGER_MODIFY_MENU(true))
	}

	const onDeletingMenuDirSubmit = () => {
		onCloseModal()
		if (isAllowDeleteDir) {
			reduxDispatch(adminActions.TRIGGER_DELETE_MENU(true))
		}
	}

	const onDeletingMenuObjectSubmit = () => {
		onCloseModal()
		if (isAllowDeleteObject) {
			reduxDispatch(adminActions.TRIGGER_DELETE_MENU(true))
		}
	}

	const onNodeClick = (node: MenuTreeNodeType) => {
		onMenuTreeRadioChange(node.id)
	}

	return (
		<Modal
			show={showModal}
			target={modalInfo.target}
			title={modalInfo.title}
			onCloseModal={onCloseModal}
			closeTitle={closeTitle}
			submitTitle={submitTitle}
			isTitleDanger={['deletingDirSubmit', 'deletingObjectSubmit'].includes(
				modalInfo.target,
			)}
			closeStatus="inherit"
			submitIconColor={
				['deletingDirSubmit', 'deletingObjectSubmit'].includes(modalInfo.target)
					? pink['A400']
					: blue['A400']
			}
			submitStatus={
				['deletingDirSubmit', 'deletingObjectSubmit'].includes(modalInfo.target)
					? 'error'
					: 'primary'
			}
			showSubmit={showSubmit}
			onSubmit={onSubmit}
			style={[
				modalInfo.target === 'menuTree'
					? {
							width: '30rem',
							height: '30rem',
					  }
					: modalInfo.target === 'iconPicker'
					? {
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'flex-start',
							width: 450,
							maxHeight: 470,
					  }
					: {},
			]}
		>
			{modalInfo.target === 'menuTree' ? (
				<View css={style}>
					<View className="menuRootDirView">
						<Pressable
							className="menuRootDirButton"
							onClick={() => onMenuTreeRadioChange('root')}
						>
							<Icon name="folder" className="menuRootDirIcon" />
							<Text className="menuRootDirText">/ (최상위 폴더)</Text>
						</Pressable>
						<View className="menuRootDirRadiobutton">
							<Radiobutton
								value={'root'}
								onRadioChange={onMenuTreeRadioChange}
								selectedValue={tempSelectedMenuParent}
							/>
						</View>
					</View>
					<DndTree
						data={[...menuTreeList]}
						rootId="root"
						showCheckBox
						hideObject
						disableDragging
						selectedRadio={tempSelectedMenuParent}
						onRadioChange={onMenuTreeRadioChange}
						onNodeClick={onNodeClick}
					/>
				</View>
			) : modalInfo.target === 'iconPicker' ? (
				<IconPicker
					onIconClick={iconName => {
						onIconPickerSubmit(iconName)
					}}
				/>
			) : modalInfo.target === 'duplicatedNameAlert' ? (
				<View>
					<Text category="p2" hint="600">
						같은 경로에 동일한 폴더 이름을 사용할 수 없습니다.
					</Text>

					<View className="recommendedMenuNameView">
						<Text category="c1" hint="600">
							추천 폴더이름 :
						</Text>
						<Pressable
							className={`recommendedMenuNameButton ${
								isCheckedRecommendedName ? 'checked' : ''
							}`}
							onClick={() => setIsCheckedRecommendedName(!isCheckedRecommendedName)}
						>
							<View className="checkIconView">
								<Icon name="check-bold" className="checkIcon" />
							</View>
							<Text>{unduplicatedName}</Text>
						</Pressable>

						<Text category="c2" className="checkIndicatorText">
							{isCheckedRecommendedName
								? `'${unduplicatedName}' 폴더 이름이 선택 되었습니다.`
								: '선택된 것이 없습니다.'}
						</Text>
					</View>
				</View>
			) : modalInfo.target === 'duplicatedNameWithMovingAlert' ? (
				<View>
					<Text category="p2" hint="600">
						중복되는 이름이 발견되어 이동할 수 없습니다.
					</Text>
					<Text category="p2" hint="600">
						이동할 폴더나 아이템의 이름을 변경하고 다시 시도해 보세요.
					</Text>
				</View>
			) : modalInfo.target === 'updatingSubmit' ? (
				<View>
					<Text category="p2" hint="600">
						변경된 내용을 저장하시겠습니까?
					</Text>
				</View>
			) : modalInfo.target === 'deletingDirSubmit' ? (
				<View>
					{isAllowDeleteDir ? (
						<>
							<Text category="p2" hint="600">
								하위 폴더들은 같이 삭제되며
							</Text>
							<Text category="p2" hint="600">
								하위의 아이템 (웹사이트 컨텐츠)는 삭제되지 않고
							</Text>
							<Text category="p2" hint="600">
								비활성 상태로 최상위 폴더로 이동됩니다.
							</Text>
							<Text category="p2" hint="600">
								{"정말 이 메뉴를 '삭제' 하시겠습니까?"}
							</Text>
						</>
					) : (
						<Text category="p2" hint="600">
							권한이 없습니다.
						</Text>
					)}
				</View>
			) : modalInfo.target === 'deletingObjectSubmit' ? (
				<View>
					{isAllowDeleteObject ? (
						<>
							<Text category="p2" hint="600">
								이 메뉴 오브젝트를 삭제하면 메뉴에서 사라집니다.
							</Text>
							<Text category="p2" hint="600">
								삭제를 진행하면 복구할 수 없습니다.
							</Text>
							<Text category="p2" hint="600">
								{"정말 이 메뉴 오브젝트를 '삭제' 하시겠습니까?"}
							</Text>
						</>
					) : (
						<Text category="p2" hint="600">
							권한이 없습니다.
						</Text>
					)}
				</View>
			) : null}
		</Modal>
	)
}

const style = css({
	'.menuRootDirView': {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		width: '100%',
		height: '2rem',

		'.menuRootDirButton': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-start',
			height: '2rem',

			'.menuRootDirIcon': {
				color: grey[400],
			},

			'.menuRootDirText': {
				marginLeft: '0.3rem',
			},
		},

		'.menuRootDirRadiobutton': {
			position: 'absolute',
			right: '.5rem',
		},
	},
	'.recommendedMenuNameView': {
		minWidth: '20rem',
		marginTop: '2rem',

		'.recommendedMenuNameButton': {
			position: 'relative',
			width: '100%',
			border: `1px solid ${deepPurple['A400']}`,
			borderRadius: '5px',
			color: deepPurple['A400'],

			'.checkIconView': {
				position: 'absolute',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '2rem',
				height: '2rem',
				left: 'o.5rem',
				top: 0,
				bottom: 0,
				margin: 'auto',
				borderRadius: '50%',
				background: deepPurple[50],

				'.checkIcon': {
					color: deepPurple[100],
					fontSize: '28px',
				},
			},

			'&.checked': {
				background: deepPurple['A400'],
				color: '#FFF',

				'.checkIconView': {
					background: '#FFF',

					'.checkIcon': {
						color: deepPurple['A400'],
					},
				},
			},
		},

		'.checkIndicatorText': {
			marginTop: '1rem',
			textAlign: 'center',
		},
	},
})
